import React, {useMemo, useState} from 'react';
import type {ExecutorNotification} from '@rocket-ui/lk-api';
import {useFetchNotifications, useMarkNotificationsRead} from '@rocket-ui/lk-api';
import {NotificationDetailsDrawer} from './NotificationDetailsDrawer';

function formatNotificationDate(value: string): string {
	const date = new Date(value);

	if (Number.isNaN(date.getTime())) return '';

	return new Intl.DateTimeFormat('ru-RU', {
		day: 'numeric',
		month: 'long',
		year: 'numeric',
	})
		.format(date)
		.replace(/\s*г\.?$/, '');
}

function notificationContent(notification: ExecutorNotification): {
	title: string;
	text?: string;
	iconUrl?: string;
} {
	const payload = notification.action ?? notification.default;
	const title = payload?.title || payload?.body || 'Уведомление';
	const text = payload?.title ? payload.body || payload.subtitle : payload?.subtitle;

	return {title, text, iconUrl: payload?.icon?.url};
}

function RocketMark(): React.ReactElement {
	return (
		<svg viewBox="0 0 30 20" className="h-4 w-6" fill="none" aria-hidden>
			<path
				d="M0 0V2.50637L15.0012 3.91585L28.2241 5.04116C28.3039 5.04684 28.3039 5.16619 28.2241 5.17188L15.0012 6.31992L0 7.75781V12.2249L15.0012 13.6628L28.2241 14.8109C28.3039 14.8166 28.3039 14.9359 28.2241 14.9416L15.0012 16.0669L0 17.4764V19.9828L30.0023 17.1127V12.6342L15.0012 11.2019L1.70986 10.0482C1.63006 10.0425 1.63006 9.92318 1.70986 9.9175L15.0012 8.76377L30.0023 7.33156V2.85306L0 0Z"
				fill="currentColor"
			/>
		</svg>
	);
}

function NotificationAvatar({src}: {src?: string}): React.ReactElement {
	return (
		<span className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-full bg-white">
			{src ? (
				// Remote icon URL from the notifications API; the lk app is Vite, not Next image optimization.
				// eslint-disable-next-line @next/next/no-img-element
				<img src={src} alt="" className="h-8 w-8 object-contain" />
			) : (
				<RocketMark />
			)}
		</span>
	);
}

export function NotificationsList(): React.ReactElement {
	const {data, isLoading, isError, hasNextPage, isFetchingNextPage, fetchNextPage, refetch} = useFetchNotifications();
	const [selected, setSelected] = useState<ExecutorNotification | null>(null);
	const {mutate: markRead} = useMarkNotificationsRead();
	const items = useMemo(() => data?.pages.flatMap((page) => page.notifications ?? []) ?? [], [data]);

	const openNotification = (notification: ExecutorNotification) => {
		setSelected(notification);
		if (!notification.isRead) {
			markRead([notification.id]);
		}
	};

	if (isLoading) {
		return <p className="text-base text-black/60">Загрузка…</p>;
	}

	if (isError) {
		return (
			<div>
				<p className="text-base text-black/70">Не удалось загрузить уведомления</p>
				<button type="button" onClick={() => refetch()} className="mt-3 text-base font-medium text-black underline">
					Повторить
				</button>
			</div>
		);
	}

	if (items.length === 0) {
		return <p className="text-base text-black/60">Нет уведомлений</p>;
	}

	return (
		<>
			<ul className="flex flex-col gap-[18px]">
				{items.map((notification) => {
					const content = notificationContent(notification);

					return (
						<li key={notification.id}>
							<button
								type="button"
								onClick={() => openNotification(notification)}
								className="flex w-full cursor-pointer items-start gap-4 border-0 bg-transparent p-0 text-left font-[inherit] text-inherit"
							>
								<NotificationAvatar src={content.iconUrl} />
								<div className="min-w-0 flex-1">
									<p className="text-[14px] font-medium leading-5 text-black">{content.title}</p>
									{content.text ? <p className="mt-1 text-[12px] leading-4 text-black/50">{content.text}</p> : null}
								</div>
								<div className="flex shrink-0 flex-col items-end pt-0.5">
									{notification.createdAt ? (
										<p className="text-[12px] leading-4 text-black/45">
											{formatNotificationDate(notification.createdAt)}
										</p>
									) : null}
									{notification.isRead ? null : (
										<>
											<span className="mt-1 h-2.5 w-2.5 bg-black" aria-hidden />
											<span className="sr-only">Непрочитано</span>
										</>
									)}
								</div>
							</button>
						</li>
					);
				})}
				{hasNextPage ? (
					<li>
						<button
							type="button"
							onClick={() => fetchNextPage()}
							disabled={isFetchingNextPage}
							className="text-base font-medium text-black underline disabled:opacity-50"
						>
							{isFetchingNextPage ? 'Загрузка…' : 'Показать ещё'}
						</button>
					</li>
				) : null}
			</ul>
			<NotificationDetailsDrawer notification={selected} onClose={() => setSelected(null)} />
		</>
	);
}
